/* eslint-disable import/no-unresolved */
import React, { useCallback, useContext, useMemo } from 'react';
import { Currency } from 'common/types/format';
import { CustomSelect } from 'components';
import { MenuPosition, OptionType } from 'components/CustomSelect/types';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import withTooltip from 'components/WithTooltip';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { getStringValue } from 'utilities';

interface ObjectOptions {
  displayKey: string;
  valueKey: string;
}

interface HandleOptionsNumberFormatParams {
  options: any[];
  format: any;
  currency: Currency;
  value: any;
}

interface GridSelectProps {
  cell: ICell;
  options: OptionType[];
  enumerated?: boolean;
  objectOptions?: ObjectOptions;
  useHandleChange?: boolean;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  menuPosition?: MenuPosition;
  handleOptionsNumberFormat?: (params: HandleOptionsNumberFormatParams) => OptionType;
  onCommit?: () => void;
  onRevert?: () => void;
  usePortal?: boolean;
  optionsTextAlign?: 'left' | 'right' | 'center';
  isMulti?: boolean;
}

const GridSelect: React.FC<GridSelectProps> = ({
  cell,
  options,
  enumerated,
  objectOptions,
  useHandleChange = true,
  menuPlacement,
  menuPosition,
  handleOptionsNumberFormat,
  isMulti = false,
  ...restProps
}) => {
  const { format, onCellsChanged } = useContext(FeaturedSpreadsheetContext);
  const currency = format?.currency;

  const objectList = useMemo(() => {
    if (handleOptionsNumberFormat) {
      return handleOptionsNumberFormat({
        options,
        format: cell.format,
        currency,
        value: cell.value,
      });
    }
    if (!objectOptions) {
      return options;
    }
    return options.map((option: any) => option[objectOptions.displayKey]);
  }, [options, objectOptions, cell, handleOptionsNumberFormat, currency]);

  const objectMap = useMemo(() => {
    if (objectOptions) {
      return options.reduce<Record<string, any>>((sofar, next) => {
        // eslint-disable-next-line no-param-reassign
        sofar[next[objectOptions.valueKey]] = next[objectOptions.displayKey];
        return sofar;
      }, {});
    }
    return undefined;
  }, [options, objectOptions]);

  const { onCommit, onRevert, optionsTextAlign, usePortal = false } = restProps;

  const getValueIndex = (value: any): number => options.findIndex(option => option === value);

  let cellValue: any = enumerated ? getValueIndex(cell.value) : cell.value;
  if (objectMap && cellValue) {
    cellValue = objectMap[cellValue];
  }

  const handleChange = useCallback(
    (value: any): void => {
      if (!useHandleChange) {
        return;
      }
      if (isMulti && Array.isArray(value)) {
        let newValues: any[] = [];
        if (objectOptions) {
          newValues = value.map((v: any) => {
            const idx = Number(v);
            return options[idx][objectOptions.valueKey as keyof OptionType];
          });
        } else if (options?.length && typeof options[0] === 'object') {
          newValues = value.map((v: any) => {
            const opt = options.find(o => String(o.value) === String(v));
            return opt ? (opt.value as any) : getStringValue(v);
          });
        } else {
          newValues = value.map((v: any) => getStringValue(v));
        }
        onCellsChanged([{ cell, value: newValues }]);
        return;
      }
      const newValue = objectOptions ? options[value][objectOptions.valueKey] : getStringValue(value);
      onCellsChanged([{ cell, value: newValue }]);
    },
    [cell, objectOptions, onCellsChanged, options, useHandleChange, isMulti]
  );

  return (
    <CustomSelect
      id={`${cell.alias}-${cell.key}`}
      className={cell.alias}
      value={cellValue}
      options={objectList}
      onChange={handleChange}
      onCommit={onCommit}
      onRevert={onRevert}
      onEmptyValue="Select Option"
      disabled={cell.readOnly}
      enumerated={enumerated}
      isMulti={isMulti}
      menuPlacement={menuPlacement}
      menuPosition={menuPosition}
      usePortal={usePortal}
      extraStyles={
        optionsTextAlign
          ? {
            option: base => ({
              ...base,
              textAlign: optionsTextAlign,
            }),
          }
          : undefined
      }
    />
  );
};

export default withTooltip(GridSelect);
